/**
 * Markdown 内容渲染组件
 *
 * 功能：
 * - 解析并渲染 Markdown 格式文本
 * - 支持 GitHub Flavored Markdown（表格、删除线、任务列表）
 * - 继承现有主题样式（通过 CSS 变量）
 * - 安全：禁止原始 HTML，元素白名单
 */

import React from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { clsx } from 'clsx';

export interface MarkdownContentProps {
  /** Markdown 格式的文本内容 */
  content: string;
  /** 自定义 CSS 类名 */
  className?: string;
}

/**
 * 自定义渲染器，使用 CSS 变量继承主题样式
 */
const components: Components = {
  // 标题
  h1: ({ children }) => (
    <h1
      style={{
        color: 'var(--text-primary, #1a1a1a)',
        fontSize: '1.5em',
        fontWeight: 'bold',
        marginTop: '0.5em',
        marginBottom: '0.5em',
      }}
    >
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2
      style={{
        color: 'var(--text-primary, #1a1a1a)',
        fontSize: '1.3em',
        fontWeight: 'bold',
        marginTop: '0.5em',
        marginBottom: '0.5em',
      }}
    >
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3
      style={{
        color: 'var(--text-primary, #1a1a1a)',
        fontSize: '1.1em',
        fontWeight: 'bold',
        marginTop: '0.5em',
        marginBottom: '0.5em',
      }}
    >
      {children}
    </h3>
  ),

  // 段落
  p: ({ children }) => (
    <p
      style={{
        color: 'var(--text-primary, #1a1a1a)',
        marginBottom: '0.5em',
      }}
    >
      {children}
    </p>
  ),

  // 粗体
  strong: ({ children }) => (
    <strong
      style={{
        fontWeight: 'bold',
        color: 'var(--text-primary, #1a1a1a)',
      }}
    >
      {children}
    </strong>
  ),

  // 斜体
  em: ({ children }) => (
    <em
      style={{
        fontStyle: 'italic',
        color: 'var(--text-primary, #1a1a1a)',
      }}
    >
      {children}
    </em>
  ),

  // 删除线
  del: ({ children }) => (
    <del
      style={{
        textDecoration: 'line-through',
        color: 'var(--text-muted, #666)',
      }}
    >
      {children}
    </del>
  ),

  // 链接
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: 'var(--accent-primary, #3b82f6)',
        textDecoration: 'underline',
      }}
    >
      {children}
    </a>
  ),

  // 无序列表
  ul: ({ children }) => (
    <ul
      style={{
        paddingLeft: '1.5em',
        marginBottom: '0.5em',
        color: 'var(--text-primary, #1a1a1a)',
      }}
    >
      {children}
    </ul>
  ),

  // 有序列表
  ol: ({ children }) => (
    <ol
      style={{
        paddingLeft: '1.5em',
        marginBottom: '0.5em',
        color: 'var(--text-primary, #1a1a1a)',
      }}
    >
      {children}
    </ol>
  ),

  // 列表项
  li: ({ children }) => (
    <li
      style={{
        marginBottom: '0.25em',
      }}
    >
      {children}
    </li>
  ),

  // 代码块
  pre: ({ children }) => (
    <pre
      style={{
        backgroundColor: 'var(--bg-code, #f3f4f6)',
        color: 'var(--text-code, #e85d04)',
        padding: '0.75em',
        borderRadius: '0.375em',
        overflow: 'auto',
        marginBottom: '0.5em',
        fontSize: '0.875em',
      }}
    >
      {children}
    </pre>
  ),

  // 行内代码
  code: ({ children, className }) => {
    // 如果有 className，说明是在代码块中
    if (className) {
      return <code style={{ fontFamily: 'monospace' }}>{children}</code>;
    }
    // 行内代码
    return (
      <code
        style={{
          backgroundColor: 'var(--bg-code, #f3f4f6)',
          color: 'var(--text-code, #e85d04)',
          padding: '0.125em 0.375em',
          borderRadius: '0.25em',
          fontSize: '0.875em',
          fontFamily: 'monospace',
        }}
      >
        {children}
      </code>
    );
  },

  // 引用块
  blockquote: ({ children }) => (
    <blockquote
      style={{
        borderLeft: '4px solid var(--border-quote, #d1d5db)',
        paddingLeft: '1em',
        marginLeft: '0',
        color: 'var(--text-muted, #666)',
        fontStyle: 'italic',
        marginBottom: '0.5em',
      }}
    >
      {children}
    </blockquote>
  ),

  // 表格
  table: ({ children }) => (
    <div style={{ overflowX: 'auto', marginBottom: '0.5em' }}>
      <table
        style={{
          borderCollapse: 'collapse',
          width: '100%',
          color: 'var(--text-primary, #1a1a1a)',
        }}
      >
        {children}
      </table>
    </div>
  ),

  // 表头
  thead: ({ children }) => (
    <thead
      style={{
        borderBottom: '2px solid var(--border-table, #e5e7eb)',
      }}
    >
      {children}
    </thead>
  ),

  // 表格主体
  tbody: ({ children }) => <tbody>{children}</tbody>,

  // 表格行
  tr: ({ children }) => (
    <tr
      style={{
        borderBottom: '1px solid var(--border-table, #e5e7eb)',
      }}
    >
      {children}
    </tr>
  ),

  // 表头单元格
  th: ({ children }) => (
    <th
      style={{
        padding: '0.5em',
        textAlign: 'left',
        fontWeight: 'bold',
      }}
    >
      {children}
    </th>
  ),

  // 表格单元格
  td: ({ children }) => (
    <td
      style={{
        padding: '0.5em',
      }}
    >
      {children}
    </td>
  ),

  // 图片
  img: ({ src, alt }) => (
    <img
      src={src}
      alt={alt}
      style={{
        maxWidth: '100%',
        height: 'auto',
        borderRadius: '8px',
        marginTop: '0.5em',
        marginBottom: '0.5em',
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        display: 'block',
      }}
    />
  ),
};

/**
 * Markdown 内容渲染组件
 */
export const MarkdownContent: React.FC<MarkdownContentProps> = ({
  content,
  className,
}) => {
  return (
    <div className={clsx('markdown-content', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components}
        skipHtml={true}
        allowedElements={[
          'h1',
          'h2',
          'h3',
          'h4',
          'h5',
          'h6',
          'p',
          'br',
          'strong',
          'em',
          'del',
          'a',
          'ul',
          'ol',
          'li',
          'pre',
          'code',
          'blockquote',
          'table',
          'thead',
          'tbody',
          'tr',
          'th',
          'td',
          'img',
        ]}
        unwrapDisallowed={false}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
